import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Filter, X, Grid3X3, LayoutGrid, SlidersHorizontal, ArrowUpDown } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/seo/SEO';
import ProductGrid from '../components/products/ProductGrid';
import ProductGridSkeleton from '../components/products/ProductGridSkeleton';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import { formatCurrency } from '../lib/currency';
import { cn } from '../lib/utils';
import { getCategoryById, getCategories, flattenCategories } from '../services/categories';
import { getProductsByCategory, getProductsBySubcategory } from '../services/products';
import { useToast } from '../hooks/use-toast';
import { BRAND_NAME } from '../lib/brand';
import { getCategoryIntro } from '../lib/categoryCopy';

const PAGE_GUTTER = 'px-3 sm:px-4 md:px-6 lg:px-8';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Yeni Eklenenler' },
  { value: 'price-asc', label: 'Fiyat: Artan' },
  { value: 'price-desc', label: 'Fiyat: Azalan' },
  { value: 'rating', label: 'En Yüksek Puan' },
  { value: 'bestselling', label: 'En Çok Satanlar' },
];

const CategoryPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { categoryId, subcategoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 9000]);
  const [gridCols, setGridCols] = useState(4);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [catData, allCats] = await Promise.all([
          getCategoryById(categoryId),
          getCategories(),
        ]);
        setCategory(catData);
        setAllCategories(allCats);

        if (subcategoryId) {
          const subId = String(subcategoryId);
          const fromChildren = catData.children?.find((c) => String(c.id) === subId);

          if (fromChildren) {
            setSubcategory(fromChildren);
          } else {
            try {
              const subCatData = await getCategoryById(subId);
              setSubcategory(subCatData);
            } catch (error) {
              console.error('Error fetching subcategory:', error);
              setSubcategory(null);
            }
          }
        } else {
          setSubcategory(null);
        }

        let fetchedProducts = [];
        if (subcategoryId) {
          fetchedProducts = await getProductsBySubcategory(categoryId, subcategoryId);
        } else {
          fetchedProducts = await getProductsByCategory(categoryId);
        }
        setProducts(fetchedProducts);
      } catch (error) {
        console.error('Error fetching category data:', error);
        toast({
          title: 'Hata',
          description: 'Kategori yüklenirken bir sorun oluştu.',
          variant: 'destructive',
        });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    if (categoryId) {
      fetchData();
    }
  }, [categoryId, subcategoryId, navigate, toast]);

  const flatCategories = useMemo(
    () => flattenCategories(allCategories),
    [allCategories]
  );

  const resolvedSubcategory = useMemo(() => {
    if (!subcategoryId) return null;

    const subId = String(subcategoryId);

    if (subcategory && String(subcategory.id) === subId) {
      return subcategory;
    }

    const fromChildren = category?.children?.find((c) => String(c.id) === subId);
    if (fromChildren) return fromChildren;

    return flatCategories.find((c) => String(c.id) === subId) || null;
  }, [subcategory, subcategoryId, category, flatCategories]);

  const subcategories = useMemo(() => {
    if (category?.children?.length) {
      return category.children;
    }
    return flatCategories.filter((cat) => String(cat.parentId) === String(categoryId));
  }, [flatCategories, category, categoryId]);

  const availableSizes = useMemo(() => {
    const sizesSet = new Set();
    products.forEach((p) => {
      if (p.sizes && Array.isArray(p.sizes)) {
        p.sizes.forEach((s) => sizesSet.add(s));
      }
    });
    return Array.from(sizesSet).sort();
  }, [products]);

  const availableColors = useMemo(() => {
    const colorsMap = new Map();
    products.forEach((p) => {
      if (p.colorNames && Array.isArray(p.colorNames)) {
        p.colorNames.forEach((colorName, index) => {
          if (!colorsMap.has(colorName) && p.colors && p.colors[index]) {
            colorsMap.set(colorName, p.colors[index]);
          }
        });
      }
    });
    return Array.from(colorsMap.entries()).map(([name, value]) => ({ name, value }));
  }, [products]);

  const priceBounds = useMemo(() => {
    const prices = products.map((p) => p.price || 0).filter((p) => p > 0);
    if (prices.length === 0) {
      return { min: 0, max: 1000 };
    }
    const min = Math.floor(Math.min(...prices));
    const max = Math.ceil(Math.max(...prices));
    return { min, max: Math.max(max, min + 10) };
  }, [products]);

  useEffect(() => {
    setPriceRange([priceBounds.min, priceBounds.max]);
  }, [priceBounds.min, priceBounds.max]);

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    if (selectedSizes.length > 0) {
      filtered = filtered.filter(
        (p) => p.sizes && p.sizes.some((s) => selectedSizes.includes(s))
      );
    }

    if (selectedColors.length > 0) {
      filtered = filtered.filter(
        (p) => p.colorNames && p.colorNames.some((c) => selectedColors.includes(c))
      );
    }

    filtered = filtered.filter(
      (p) => (p.price || 0) >= priceRange[0] && (p.price || 0) <= priceRange[1]
    );

    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-desc':
        filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'bestselling':
        filtered.sort((a, b) => (b.reviews || 0) - (a.reviews || 0));
        break;
      default:
        filtered.sort((a, b) => {
          const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bDate - aDate;
        });
    }

    return filtered;
  }, [products, selectedSizes, selectedColors, priceRange, sortBy]);

  const toggleSize = (size) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
    );
  };

  const toggleColor = (color) => {
    setSelectedColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  };

  const clearFilters = () => {
    setSelectedSizes([]);
    setSelectedColors([]);
    setPriceRange([priceBounds.min, priceBounds.max]);
  };

  const hasActiveFilters =
    selectedSizes.length > 0 ||
    selectedColors.length > 0 ||
    priceRange[0] > priceBounds.min ||
    priceRange[1] < priceBounds.max;

  const activeFilterCount =
    selectedSizes.length +
    selectedColors.length +
    (priceRange[0] > priceBounds.min || priceRange[1] < priceBounds.max ? 1 : 0);

  const showFilters = availableSizes.length > 0 || availableColors.length > 0;
  const categoryName = category?.name || categoryId;
  const subcategoryName = resolvedSubcategory?.name || null;
  const pageTitle = subcategoryName || categoryName;
  const sortLabel = SORT_OPTIONS.find((o) => o.value === sortBy)?.label || 'Sırala';
  const { intro: pageIntro } = getCategoryIntro({
    category,
    subcategory: resolvedSubcategory,
  });

  const FilterSection = ({ className }) => (
    <div className={cn('space-y-1', className)}>
      <Accordion
        type="multiple"
        defaultValue={['sizes', 'colors', 'price']}
        className="w-full"
      >
        {availableSizes.length > 0 && (
          <AccordionItem value="sizes" className="border-neutral-200">
            <AccordionTrigger className="py-4 text-xs font-medium uppercase tracking-[0.14em] text-neutral-900 hover:no-underline">
              {t('product.size')}
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-wrap gap-2 pb-2">
                {availableSizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => toggleSize(size)}
                    className={cn(
                      'min-w-[2.5rem] px-3 py-2 text-xs font-medium tracking-wide transition-colors',
                      selectedSizes.includes(size)
                        ? 'bg-neutral-900 text-white'
                        : 'border border-neutral-200 text-neutral-700 hover:border-neutral-400'
                    )}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {availableColors.length > 0 && (
          <AccordionItem value="colors" className="border-neutral-200">
            <AccordionTrigger className="py-4 text-xs font-medium uppercase tracking-[0.14em] text-neutral-900 hover:no-underline">
              {t('product.color')}
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 pb-2">
                {availableColors.map((color) => (
                  <div key={color.name} className="flex items-center gap-3">
                    <Checkbox
                      id={`color-${color.name}`}
                      checked={selectedColors.includes(color.name)}
                      onCheckedChange={() => toggleColor(color.name)}
                    />
                    <div
                      className="h-4 w-4 shrink-0 rounded-full border border-neutral-200"
                      style={{ backgroundColor: color.value }}
                    />
                    <Label
                      htmlFor={`color-${color.name}`}
                      className="cursor-pointer text-sm font-normal text-neutral-700"
                    >
                      {color.name}
                    </Label>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        <AccordionItem value="price" className="border-neutral-200">
          <AccordionTrigger className="py-4 text-xs font-medium uppercase tracking-[0.14em] text-neutral-900 hover:no-underline">
            Fiyat
          </AccordionTrigger>
          <AccordionContent>
            <Slider
              value={priceRange}
              onValueChange={setPriceRange}
              min={priceBounds.min}
              max={priceBounds.max}
              step={10}
              className="mb-4"
            />
            <div className="flex justify-between pb-2 text-xs tabular-nums text-neutral-500">
              <span>{formatCurrency(priceRange[0])}</span>
              <span>{formatCurrency(priceRange[1])}</span>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {hasActiveFilters && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="mt-4 w-full rounded-none border-neutral-300 text-xs uppercase tracking-[0.12em]"
        >
          {t('common.clearFilters')}
        </Button>
      )}
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <SEO title={`${BRAND_NAME} | Kategori`} description="Ürünleri keşfedin" />
        <div className="w-full max-w-[1600px] mx-auto py-6 sm:py-8">
          <div className={cn(PAGE_GUTTER, 'category-page-hero mb-6')}>
            <div className="mx-auto mb-5 flex justify-center sm:mb-6">
              <div className="h-3 w-48 animate-pulse rounded bg-neutral-100" />
            </div>
            <div className="mx-auto mb-4 h-9 w-56 max-w-full animate-pulse rounded bg-neutral-100 sm:h-11 sm:w-64" />
            <div className="mx-auto mb-5 h-px w-10 animate-pulse bg-neutral-200" />
            <div className="mx-auto h-4 max-w-sm animate-pulse rounded bg-neutral-100" />
            <div className="mx-auto mt-3 h-4 max-w-xs animate-pulse rounded bg-neutral-100" />
          </div>
          <ProductGridSkeleton columns={4} count={12} />
        </div>
      </Layout>
    );
  }

  if (!category) {
    return (
      <Layout>
        <SEO title={`${BRAND_NAME} | Kategori Bulunamadı`} description="Kategori bulunamadı" />
        <div className={cn('w-full max-w-[1600px] mx-auto py-16 text-center', PAGE_GUTTER)}>
          <h1 className="mb-4 text-2xl font-semibold">Kategori bulunamadı</h1>
          <Button onClick={() => navigate('/')}>Ana Sayfaya Dön</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title={`${BRAND_NAME} | ${pageTitle}`}
        description={pageIntro}
      />
      <div className="w-full max-w-[1600px] mx-auto pb-10 sm:pb-12">
        {/* Premium centered page hero */}
        <header className="category-page-hero">
          <Breadcrumb className="category-breadcrumb">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="text-[11px] uppercase tracking-[0.12em] text-neutral-400">
                    {t('common.home')}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {resolvedSubcategory ? (
                  <BreadcrumbLink asChild>
                    <Link
                      to={`/category/${categoryId}`}
                      className="text-[11px] uppercase tracking-[0.12em] text-neutral-400"
                    >
                      {categoryName}
                    </Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage className="text-[11px] uppercase tracking-[0.12em] text-neutral-900">
                    {categoryName}
                  </BreadcrumbPage>
                )}
              </BreadcrumbItem>
              {resolvedSubcategory && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="text-[11px] uppercase tracking-[0.12em] text-neutral-900">
                      {subcategoryName}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )}
            </BreadcrumbList>
          </Breadcrumb>

          {resolvedSubcategory && (
            <p className="category-page-eyebrow">{categoryName}</p>
          )}

          <h1 className="category-page-title">{pageTitle}</h1>

          <div className="category-page-divider" aria-hidden="true" />

          <p className="category-page-intro">{pageIntro}</p>

          <p className="category-page-count">
            {filteredProducts.length}{' '}
            {filteredProducts.length === 1 ? t('cart.item') : t('cart.items')}
          </p>
        </header>

        {/* Subcategory navigation */}
        {subcategories.length > 0 && (
          <nav
            aria-label="Alt kategoriler"
            className={cn(
              PAGE_GUTTER,
              'mb-5 flex justify-center gap-5 overflow-x-auto border-b border-neutral-200 pb-0 scrollbar-hide sm:mb-6 sm:gap-8'
            )}
          >
            <Link
              to={`/category/${categoryId}`}
              className={cn(
                'shrink-0 border-b-2 pb-3 text-sm font-medium uppercase tracking-[0.12em] transition-colors',
                !subcategoryId
                  ? 'border-neutral-900 text-neutral-900'
                  : 'border-transparent text-neutral-400 hover:text-neutral-700'
              )}
            >
              Tümü
            </Link>
            {subcategories.map((sub) => (
              <Link
                key={sub.id}
                to={`/category/${categoryId}/${sub.id}`}
                className={cn(
                  'shrink-0 border-b-2 pb-3 text-sm font-medium uppercase tracking-[0.12em] transition-colors',
                  String(subcategoryId) === String(sub.id)
                    ? 'border-neutral-900 text-neutral-900'
                    : 'border-transparent text-neutral-400 hover:text-neutral-700'
                )}
              >
                {sub.name}
              </Link>
            ))}
          </nav>
        )}

        <div className="flex gap-0 lg:gap-10 xl:gap-14">
          {/* Desktop filters */}
          {showFilters && (
            <aside className={cn('hidden lg:block lg:w-56 xl:w-60 shrink-0', PAGE_GUTTER)}>
              <div className="sticky top-28 pt-1">
                <div className="mb-5 flex items-center gap-2 border-b border-neutral-200 pb-4">
                  <SlidersHorizontal size={14} className="text-neutral-500" strokeWidth={1.5} />
                  <h2 className="text-xs font-medium uppercase tracking-[0.14em] text-neutral-900">
                    {t('common.filters')}
                  </h2>
                  {activeFilterCount > 0 && (
                    <span className="ml-auto flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-neutral-900 px-1.5 text-[10px] font-medium text-white">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                <FilterSection />
              </div>
            </aside>
          )}

          <div className="min-w-0 flex-1">
            {/* Sticky toolbar */}
            <div className="sticky top-14 z-30 border-b border-neutral-200 bg-white/95 backdrop-blur-sm sm:top-16">
              <div
                className={cn(
                  PAGE_GUTTER,
                  'flex h-12 items-center justify-between gap-3 sm:h-14'
                )}
              >
                <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                  {showFilters && (
                    <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                      <SheetTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 shrink-0 rounded-none border-neutral-300 px-3 text-xs uppercase tracking-[0.1em] lg:hidden"
                        >
                          <Filter size={14} className="mr-1.5" strokeWidth={1.5} />
                          {t('common.filters')}
                          {activeFilterCount > 0 && (
                            <span className="ml-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-neutral-900 px-1 text-[9px] font-medium text-white">
                              {activeFilterCount}
                            </span>
                          )}
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="left" className="w-[min(100vw-2rem,320px)] p-0">
                        <SheetHeader className="border-b border-neutral-200 px-5 py-4 text-left">
                          <SheetTitle className="text-xs font-medium uppercase tracking-[0.14em]">
                            {t('common.filters')}
                          </SheetTitle>
                        </SheetHeader>
                        <div className="overflow-y-auto px-5 py-2">
                          <FilterSection />
                        </div>
                      </SheetContent>
                    </Sheet>
                  )}

                  <span className="hidden text-xs uppercase tracking-[0.12em] text-neutral-400 sm:inline">
                    {filteredProducts.length} ürün
                  </span>
                </div>

                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                  <div className="hidden items-center rounded-none border border-neutral-200 p-0.5 md:flex">
                    <button
                      type="button"
                      onClick={() => setGridCols(3)}
                      className={cn(
                        'p-2 transition-colors',
                        gridCols === 3
                          ? 'bg-neutral-100 text-neutral-900'
                          : 'text-neutral-400 hover:text-neutral-700'
                      )}
                      aria-label="3 sütun"
                    >
                      <Grid3X3 size={16} strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setGridCols(4)}
                      className={cn(
                        'p-2 transition-colors',
                        gridCols === 4
                          ? 'bg-neutral-100 text-neutral-900'
                          : 'text-neutral-400 hover:text-neutral-700'
                      )}
                      aria-label="4 sütun"
                    >
                      <LayoutGrid size={16} strokeWidth={1.5} />
                    </button>
                  </div>

                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="h-9 w-[9.5rem] rounded-none border-neutral-300 text-xs sm:h-10 sm:w-[11rem]">
                      <ArrowUpDown
                        size={13}
                        className="mr-1.5 shrink-0 text-neutral-400"
                        strokeWidth={1.5}
                      />
                      <SelectValue placeholder="Sırala">{sortLabel}</SelectValue>
                    </SelectTrigger>
                    <SelectContent align="end">
                      {SORT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active filter chips */}
              {hasActiveFilters && (
                <div
                  className={cn(
                    PAGE_GUTTER,
                    'flex items-center gap-2 overflow-x-auto border-t border-neutral-100 py-2.5 scrollbar-hide'
                  )}
                >
                  <span className="shrink-0 text-[10px] uppercase tracking-[0.12em] text-neutral-400">
                    Seçili
                  </span>
                  {selectedSizes.map((size) => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => toggleSize(size)}
                      className="inline-flex shrink-0 items-center gap-1.5 border border-neutral-200 px-2.5 py-1 text-xs text-neutral-700 transition-colors hover:border-neutral-400"
                    >
                      {size}
                      <X size={12} strokeWidth={1.5} />
                    </button>
                  ))}
                  {selectedColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => toggleColor(color)}
                      className="inline-flex shrink-0 items-center gap-1.5 border border-neutral-200 px-2.5 py-1 text-xs text-neutral-700 transition-colors hover:border-neutral-400"
                    >
                      {color}
                      <X size={12} strokeWidth={1.5} />
                    </button>
                  ))}
                  {(priceRange[0] > priceBounds.min || priceRange[1] < priceBounds.max) && (
                    <button
                      type="button"
                      onClick={() => setPriceRange([priceBounds.min, priceBounds.max])}
                      className="inline-flex shrink-0 items-center gap-1.5 border border-neutral-200 px-2.5 py-1 text-xs text-neutral-700 transition-colors hover:border-neutral-400"
                    >
                      {formatCurrency(priceRange[0])} – {formatCurrency(priceRange[1])}
                      <X size={12} strokeWidth={1.5} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="shrink-0 text-[10px] uppercase tracking-[0.12em] text-neutral-500 underline-offset-2 hover:text-neutral-900 hover:underline"
                  >
                    Temizle
                  </button>
                </div>
              )}
            </div>

            {/* Products — grid layout untouched */}
            {filteredProducts.length > 0 ? (
              <div className="pt-4 sm:pt-5">
                <ProductGrid products={filteredProducts} columns={gridCols} />
              </div>
            ) : (
              <div className={cn(PAGE_GUTTER, 'py-20 text-center')}>
                <p className="mb-2 text-base font-medium text-neutral-900">
                  Aradığınız kriterde ürün bulunamadı
                </p>
                <p className="mb-6 text-sm text-neutral-500">
                  Filtreleri değiştirerek tekrar deneyebilirsiniz.
                </p>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="rounded-none border-neutral-300 text-xs uppercase tracking-[0.12em]"
                  >
                    {t('common.clearFilters')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CategoryPage;
